import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Loader2, GitPullRequest, FileText } from "lucide-react";
import { useSentinelAudits, useSentinelFindings, useGenerateProposals, useRunAudit } from "@/hooks/useSentinelCycle";
import { FindingCard } from "./FindingCard";
import type { Database } from "@/integrations/supabase/types";

type Audit = Database["public"]["Tables"]["sentinel_audits"]["Row"];
type Finding = Database["public"]["Tables"]["sentinel_findings"]["Row"];

interface Props {
  orgId?: string;
}

export default function SentinelAudits({ orgId }: Props) {
  const { data: audits = [], isLoading: loadingAudits } = useSentinelAudits();
  const { data: findings = [] } = useSentinelFindings();
  const generateProposals = useGenerateProposals();
  const runAudit = useRunAudit();

  const [selectedAudit, setSelectedAudit] = useState<Audit | null>(null);
  const [selectedFinding, setSelectedFinding] = useState<Finding | null>(null);
  const [search, setSearch] = useState("");
  const [severityFilter, setSeverityFilter] = useState("all");

  const filteredFindings = useMemo(() => {
    return (findings as Finding[]).filter((f) => {
      if (severityFilter !== "all" && f.severity !== severityFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        return (
          f.dimension?.toLowerCase().includes(q) ||
          f.category?.toLowerCase().includes(q) ||
          f.remediation_hint?.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [findings, severityFilter, search]);

  const auditFindings = useMemo(() => {
    if (!selectedAudit) return [] as Finding[];
    const inline = (selectedAudit.findings as unknown as Finding[] | null) ?? [];
    return Array.isArray(inline) ? inline : [];
  }, [selectedAudit]);

  return (
    <div className="space-y-4">
      {/* Header actions */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" aria-hidden="true" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por dimensão, categoria ou hint..."
            className="pl-8 h-9"
            aria-label="Buscar findings"
          />
        </div>
        <Select value={severityFilter} onValueChange={setSeverityFilter}>
          <SelectTrigger className="w-full sm:w-40 h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas severidades</SelectItem>
            <SelectItem value="critical">Crítica</SelectItem>
            <SelectItem value="high">Alta</SelectItem>
            <SelectItem value="medium">Média</SelectItem>
            <SelectItem value="low">Baixa</SelectItem>
          </SelectContent>
        </Select>
        <Button
          size="sm"
          disabled={runAudit.isPending || !orgId}
          onClick={() => orgId && runAudit.mutate({ org_id: orgId })}
        >
          {runAudit.isPending
            ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
            : <Search className="w-4 h-4 mr-1.5" aria-hidden="true" />}
          Nova Auditoria
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Audits list */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <FileText className="w-4 h-4 text-primary" aria-hidden="true" />
              Auditorias ({audits.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 max-h-[60vh] overflow-y-auto">
            {loadingAudits ? (
              <><Skeleton className="h-16 w-full" /><Skeleton className="h-16 w-full" /></>
            ) : audits.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                Nenhuma auditoria ainda. Clique em "Nova Auditoria".
              </p>
            ) : (
              (audits as Audit[]).map((audit) => {
                const inline = (audit.findings as unknown as Finding[] | null) ?? [];
                const auditFindingCount = Array.isArray(inline) ? inline.length : 0;
                const isSelected = selectedAudit?.id === audit.id;
                return (
                  <button
                    type="button"
                    key={audit.id}
                    onClick={() => setSelectedAudit(audit)}
                    className={`w-full text-left p-3 rounded-md border transition-colors ${
                      isSelected ? "border-primary bg-primary/5" : "border-border/60 hover:border-primary/40"
                    }`}
                    aria-pressed={isSelected}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="text-sm font-medium truncate">{audit.audit_type}</span>
                      <Badge variant="outline" className="text-xs">{auditFindingCount} findings</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {new Date(audit.created_at).toLocaleString("pt-BR")}
                    </p>
                  </button>
                );
              })
            )}
          </CardContent>
        </Card>

        {/* Findings list */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">
              {selectedAudit ? `Findings da auditoria selecionada` : `Todos os findings (${filteredFindings.length})`}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 max-h-[60vh] overflow-y-auto">
            {(selectedAudit ? auditFindings : filteredFindings).length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                Nenhum finding encontrado.
              </p>
            ) : (
              (selectedAudit ? auditFindings : filteredFindings).map((f) => (
                <FindingCard key={f.id} finding={f} onClick={() => setSelectedFinding(f)} />
              ))
            )}
          </CardContent>
        </Card>
      </div>

      {/* Audit detail Sheet */}
      <Sheet open={!!selectedAudit} onOpenChange={(open) => !open && setSelectedAudit(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{selectedAudit?.audit_type ?? "Auditoria"}</SheetTitle>
            <SheetDescription>
              {selectedAudit && new Date(selectedAudit.created_at).toLocaleString("pt-BR")}
            </SheetDescription>
          </SheetHeader>
          {selectedAudit && (
            <div className="mt-4 space-y-4">
              <Button
                size="sm"
                className="w-full"
                disabled={generateProposals.isPending}
                onClick={() => generateProposals.mutate(selectedAudit.id)}
              >
                {generateProposals.isPending
                  ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  : <GitPullRequest className="w-4 h-4 mr-1.5" aria-hidden="true" />}
                Gerar propostas a partir desta auditoria
              </Button>
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground">Findings ({auditFindings.length})</p>
                {auditFindings.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nenhum finding registrado.</p>
                ) : (
                  auditFindings.map((f) => (
                    <FindingCard key={f.id} finding={f} onClick={() => setSelectedFinding(f)} />
                  ))
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Finding detail Sheet */}
      <Sheet open={!!selectedFinding} onOpenChange={(open) => !open && setSelectedFinding(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Finding · {selectedFinding?.dimension}</SheetTitle>
            <SheetDescription>
              {selectedFinding?.category} · severidade {selectedFinding?.severity}
            </SheetDescription>
          </SheetHeader>
          {selectedFinding && (
            <div className="mt-4 space-y-3 text-sm">
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Score</p>
                <p className="text-2xl font-bold font-headline">{Number(selectedFinding.score).toFixed(2)}</p>
              </div>
              {selectedFinding.remediation_hint && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">Sugestão</p>
                  <p>{selectedFinding.remediation_hint}</p>
                </div>
              )}
              {selectedFinding.score_breakdown && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">Detalhamento</p>
                  <pre className="text-[11px] bg-muted p-2 rounded overflow-x-auto">
                    {JSON.stringify(selectedFinding.score_breakdown, null, 2)}
                  </pre>
                </div>
              )}
              <p className="text-[11px] text-muted-foreground">
                {new Date(selectedFinding.created_at).toLocaleString("pt-BR")}
              </p>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
